import React, { useState, useEffect } from 'react'
import Topbar from '../layouts/Topbar'
import Sidebar from '../layouts/Sidebar'
import axios from 'axios'
import Cookies from 'js-cookie'
import { FaSearch, FaFilter, FaEye, FaEdit, FaCalendarAlt, FaUser, FaCheckCircle, FaTimesCircle, FaClock, FaCommentDots, FaSpinner } from 'react-icons/fa'
import API_BASE_URL from '../../../config/api'

const HODsImprovementPlans = () => {
  const [improvementPlans, setImprovementPlans] = useState([])
  const [filteredPlans, setFilteredPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedYear, setSelectedYear] = useState('')
  const [availableYears, setAvailableYears] = useState([])
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const baseUrl = API_BASE_URL

  useEffect(() => {
    fetchImprovementPlans()
  }, [])

  useEffect(() => {
    filterPlans()
  }, [improvementPlans, searchQuery, selectedYear])

  const fetchImprovementPlans = async () => {
    try {
      setLoading(true)
      const token = Cookies.get('token')
      const response = await axios.get(`${baseUrl}/api/improvement-plans/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.data && response.data.length > 0) {
        setImprovementPlans(response.data)
        
        // Extract unique years and sort them, filtering out null/undefined values
        const years = [...new Set(response.data
          .map(plan => plan.year)
          .filter(year => year !== null && year !== undefined)
        )].sort((a, b) => b - a)
        setAvailableYears(years)
        
        // Set the most recent year as default
        if (years.length > 0) {
          setSelectedYear(years[0].toString())
        }
      } else {
        setImprovementPlans([])
      }
    } catch (error) {
      console.error('Error fetching improvement plans:', error)
      setError('Failed to fetch improvement plans')
    } finally {
      setLoading(false)
    }
  }

  const filterPlans = () => {
    let filtered = [...improvementPlans]

    // Filter by year
    if (selectedYear) {
      filtered = filtered.filter(plan => 
        plan.year !== null && 
        plan.year !== undefined && 
        plan.year.toString() === selectedYear
      )
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(plan => 
        (plan.employee_name && plan.employee_name.toLowerCase().includes(query)) ||
        (plan.employee_email && plan.employee_email.toLowerCase().includes(query)) ||
        (plan.strengths && plan.strengths.toLowerCase().includes(query)) ||
        (plan.weaknesses && plan.weaknesses.toLowerCase().includes(query)) ||
        (plan.employee_comment && plan.employee_comment.toLowerCase().includes(query)) ||
        (plan.training_needs && plan.training_needs.toLowerCase().includes(query)) ||
        (plan.supervisor_comment && plan.supervisor_comment.toLowerCase().includes(query))
      )
    }

    setFilteredPlans(filtered)
  }

  const handleViewPlan = (plan) => {
    setSelectedPlan(plan)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setSelectedPlan(null)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheckCircle className="status-icon approved" />
      case 'rejected':
        return <FaTimesCircle className="status-icon rejected" />
      case 'pending':
        return <FaClock className="status-icon pending" />
      default:
        return <FaClock className="status-icon pending" />
    }
  }

  const getStatusClass = (status) => {
    switch (status) {
      case 'approved':
        return 'status-approved'
      case 'rejected':
        return 'status-rejected'
      case 'pending':
        return 'status-pending'
      default:
        return 'status-pending'
    }
  }

  if (loading) {
    return (
      <>
      <Topbar/>
      <Sidebar/>
      <div className="employee-goals-container">
        <div className="loading-container">
          <FaSpinner className="spinner" />
        </div>
      </div>
      </>
      
    )
  }

  if (error) {
    return (
      <>
      <Topbar/>
      <Sidebar/>
      <div className="employee-goals-container">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchImprovementPlans} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
      </>
      
    )
  }

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-improvement-plans-container">
        {/* Page Header */}
        
        {/* Filters and Search */}
        <div className="filters-section">
          <div className="search-container">
            <h3>Improvement Plans</h3>
          </div>

          <div className="search-container">
            <input
              type="text"
              placeholder="Search by employee name, email, or content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-container">
            <FaFilter className="filter-icon" />
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="year-filter"
            >
              <option value="">All Years</option>
              {availableYears.map(year => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Improvement Plans Table */}
        {filteredPlans.length === 0 ? (
          <div className="no-data-container">
            <div className="no-data-icon">
              <FaCommentDots />
            </div>
            <h3>No Improvement Plans Found</h3>
            <p>
              {searchQuery || selectedYear 
                ? 'Try adjusting your search criteria or filters.'
                : 'No improvement plans have been submitted yet.'
              }
            </p>
          </div>
        ) : (
          <div className="table-container">
            <table className="improvement-plans-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Year</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlans.map((plan) => (
                  <tr key={plan.id} className="table-row">
                    <td className="employee-cell">
                      <div className="employee-info">
                        <FaUser className="employee-icon" />
                        <div className="employee-details">
                          <div className="employee-name">{plan.employee_name || 'Unknown Employee'}</div>
                          <div className="employee-email">{plan.employee_email || 'No email provided'}</div>
                        </div>
                      </div>
                    </td>
                    <td className="year-cell">
                      <div className="year-badge">
                        <FaCalendarAlt />
                        <span>{plan.year || 'N/A'}</span>
                      </div>
                    </td>
                    
                    <td className="status-cell">
                      <div className={`status-badge ${getStatusClass(plan.approval_status)}`}>
                        {getStatusIcon(plan.approval_status)}
                        <span className="status-text">
                          {plan.approval_status === 'approved' ? 'Approved' :
                           plan.approval_status === 'rejected' ? 'Rejected' : 'Pending'}
                        </span>
                      </div>
                    </td>
                    
                    <td className="actions-cell">
                      <button
                        onClick={() => handleViewPlan(plan)}
                        className="view-btn"
                        title="View full details"
                      >
                        <FaEye />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Results Summary */}
        <div className="results-summary">
          <p>
            Showing {filteredPlans.length} improvement plan{filteredPlans.length !== 1 ? 's' : ''}
            {selectedYear && ` for ${selectedYear}`}
            {searchQuery && ` matching "${searchQuery}"`}
          </p>
        </div>

        {/* Modal for viewing plan details */}
        {modalOpen && selectedPlan && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Improvement Plan Details</h2>
                <button onClick={closeModal} className="close-btn">&times;</button>
              </div>

              <div className="modal-body">
                <div className="modal-sections">
                  <div className="modal-section">
                    <h4>Strengths</h4>
                    <p>{selectedPlan.strengths || 'No strengths listed'}</p>
                  </div>

                  <div className="modal-section">
                    <h4>Weaknesses</h4>
                    <p>{selectedPlan.weaknesses || 'No weaknesses listed'}</p>
                  </div>

                  <div className="modal-section">
                    <h4>Training Needs</h4>
                    <p>{selectedPlan.training_needs || 'No training needs specified'}</p>
                  </div>

                  <div className="modal-section">
                    <h4>Employee Comment</h4>
                    <p>{selectedPlan.employee_comment || 'No comment provided'}</p>
                  </div>

                  {(selectedPlan.approval_status === 'approved' || selectedPlan.approval_status === 'rejected') && (
                    <div className="modal-section">
                      <h4>Supervisor Comment</h4>
                      <p>{selectedPlan.supervisor_comment || 'No supervisor comment provided'}</p>
                    </div>
                  )}

                  <div className="modal-section">
                    <h4>Approval Status</h4>
                    <div className={`status-badge ${getStatusClass(selectedPlan.approval_status)}`}>
                      {getStatusIcon(selectedPlan.approval_status)}
                      <span className="status-text">
                        {selectedPlan.approval_status === 'approved' ? 'Approved' :
                         selectedPlan.approval_status === 'rejected' ? 'Rejected' : 'Pending'}
                      </span>
                    </div>
                    {selectedPlan.approval_status === 'rejected' && selectedPlan.rejection_reason && (
                      <div className="rejection-reason">
                        <FaCommentDots className="reason-icon" />
                        <p>{selectedPlan.rejection_reason}</p>
                      </div>
                    )}
                  </div>

                  {selectedPlan.date_signed && (
                    <div className="modal-section">
                      <h4>Date Signed</h4>
                      <p>{formatDate(selectedPlan.date_signed)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default HODsImprovementPlans